/**
 * Facebook-ad attribution for the coach signup flow.
 *
 * A coach who clicks a Facebook ad lands here with `fbclid` (+ optional `utm_*`)
 * on the URL. We capture those once, persist them across the wizard steps in
 * sessionStorage, and hand them to the backend at account creation so it can
 * fire the Facebook Conversions API event and the GoHighLevel webhook — but
 * only for coaches who actually came from an ad (fbclid present).
 */

const STORAGE_KEY = "signupAttribution";

// URL params we carry through to the backend.
const URL_PARAMS = [
  "fbclid",
  "utm_source",
  "utm_campaign",
  "utm_medium",
  "utm_content",
  "utm_term",
];

// Fields forwarded as hidden form inputs (URL params + derived/cookie values).
export const ATTRIBUTION_FIELDS = [...URL_PARAMS, "fbc", "fbp"];

function getCookie(name) {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

/**
 * Read attribution off the current URL + cookies, merge with anything already
 * captured this session, persist, and return it. First-touch values win (we
 * never overwrite an existing param with a blank one), so navigating within the
 * wizard — where the URL no longer carries the params — doesn't lose them.
 * No-op safe on the server (returns {}).
 */
export function captureAttribution() {
  if (typeof window === "undefined") return {};

  const stored = readAttribution();
  const params = new URLSearchParams(window.location.search);
  const next = { ...stored };

  URL_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) next[key] = value;
  });

  // fbc: Facebook's click-id format, built from fbclid at capture time so the
  // timestamp reflects the actual click. Only (re)build when fbclid is fresh.
  const freshFbclid = params.get("fbclid");
  if (freshFbclid) next.fbc = `fb.1.${Date.now()}.${freshFbclid}`;

  // fbp: the _fbp browser cookie set by the Facebook pixel, if present.
  const fbp = getCookie("_fbp");
  if (fbp) next.fbp = fbp;

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // sessionStorage unavailable (private mode / disabled) — fields still get
    // returned for the current render; capture just won't survive a refresh.
  }
  return next;
}

/** Returns the persisted attribution object, or {} if none / unavailable. */
export function readAttribution() {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}
